import type {
  AiManagedGroupEligibleCampaign,
  AiManagedGroupPortfolioOption,
} from "@adpilot/shared";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type CandidateAiFilter,
  type CandidateDeliveryFilter,
  type CandidatePortfolioFilter,
  type CandidateSortDirection,
  type CandidateSortField,
  ManagedGroupCandidateFilters,
  ManagedGroupCandidatePicker,
} from "@/components/managed-group/ManagedGroupCandidatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CampaignTransferPane } from "./CampaignTransferPane";
import { CAMPAIGN_PAGE_SIZE, sortCandidates } from "./campaign-selection";

interface ScopeStepProps {
  campaignDeliveryStatus: CandidateDeliveryFilter;
  campaignPortfolioId: CandidatePortfolioFilter;
  campaignAiStatus: CandidateAiFilter;
  campaignSortBy: CandidateSortField;
  campaignSortDirection: CandidateSortDirection;
  campaignPage: number;
  campaignPageSize: number;
  campaignSearch: string;
  campaignTotal: number;
  campaigns: AiManagedGroupEligibleCampaign[];
  portfolioOptions: AiManagedGroupPortfolioOption[];
  isCampaignError: boolean;
  isPortfolioError: boolean;
  isLoading: boolean;
  name: string;
  selectedCampaignPage: number;
  selectedCampaigns: Map<string, AiManagedGroupEligibleCampaign>;
  shopId: string;
  shopItems: Array<{ value: string; label: string }>;
  onAddCampaigns: (campaigns: AiManagedGroupEligibleCampaign[]) => void;
  onCampaignDeliveryStatusChange: (status: CandidateDeliveryFilter) => void;
  onCampaignPortfolioIdChange: (portfolioId: CandidatePortfolioFilter) => void;
  onCampaignAiStatusChange: (status: CandidateAiFilter) => void;
  onCampaignSortChange: (field: CandidateSortField) => void;
  onCampaignPageChange: (page: number) => void;
  onRetryCampaigns: () => void;
  onRetryPortfolios: () => void;
  onCampaignSearchChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onRemoveCampaigns: (campaignIds: string[]) => void;
  onSelectedCampaignPageChange: (page: number) => void;
  onShopChange: (shopId: string | null) => void;
}

export function ScopeStep({
  campaignDeliveryStatus,
  campaignPortfolioId,
  campaignAiStatus,
  campaignSortBy,
  campaignSortDirection,
  campaignPage,
  campaignPageSize,
  campaignSearch,
  campaignTotal,
  campaigns,
  portfolioOptions,
  isCampaignError,
  isPortfolioError,
  isLoading,
  name,
  selectedCampaignPage,
  selectedCampaigns,
  shopId,
  shopItems,
  onAddCampaigns,
  onCampaignDeliveryStatusChange,
  onCampaignPortfolioIdChange,
  onCampaignAiStatusChange,
  onCampaignSortChange,
  onCampaignPageChange,
  onRetryCampaigns,
  onRetryPortfolios,
  onCampaignSearchChange,
  onNameChange,
  onRemoveCampaigns,
  onSelectedCampaignPageChange,
  onShopChange,
}: ScopeStepProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const selected = sortCandidates(
    [...selectedCampaigns.values()],
    campaignSortBy,
    campaignSortDirection,
  );
  const [pendingAdd, setPendingAdd] = useState<Map<string, AiManagedGroupEligibleCampaign>>(
    new Map(),
  );
  const [pendingRemove, setPendingRemove] = useState<Set<string>>(new Set());
  const selectedPageCount = Math.max(1, Math.ceil(selected.length / CAMPAIGN_PAGE_SIZE));
  const safeSelectedPage = Math.min(selectedCampaignPage, selectedPageCount);
  const selectedPageRows = selected.slice(
    (safeSelectedPage - 1) * CAMPAIGN_PAGE_SIZE,
    safeSelectedPage * CAMPAIGN_PAGE_SIZE,
  );
  const selectedAllChecked =
    selectedPageRows.length > 0 &&
    selectedPageRows.every((campaign) => pendingRemove.has(campaign.id));
  const selectedSomeChecked = selectedPageRows.some((campaign) => pendingRemove.has(campaign.id));

  useEffect(() => {
    if (selectedCampaignPage > selectedPageCount) {
      onSelectedCampaignPageChange(selectedPageCount);
    }
  }, [onSelectedCampaignPageChange, selectedCampaignPage, selectedPageCount]);

  function togglePendingAdd(campaign: AiManagedGroupEligibleCampaign): void {
    if (campaign.status === "archived") return;
    setPendingAdd((current) => {
      const next = new Map(current);
      if (next.has(campaign.id)) next.delete(campaign.id);
      else if (selected.length + next.size < 100) next.set(campaign.id, campaign);
      return next;
    });
  }

  function togglePendingRemove(campaignId: string): void {
    setPendingRemove((current) => {
      const next = new Set(current);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  }

  function toggleAllPendingAdd(
    checked: boolean,
    selectableCampaigns: AiManagedGroupEligibleCampaign[],
  ): void {
    setPendingAdd((current) => {
      const next = new Map(current);
      for (const campaign of selectableCampaigns) {
        if (!checked) {
          next.delete(campaign.id);
          continue;
        }
        if (!next.has(campaign.id) && selected.length + next.size < 100) {
          next.set(campaign.id, campaign);
        }
      }
      return next;
    });
  }

  function toggleAllPendingRemove(checked: boolean): void {
    setPendingRemove((current) => {
      const next = new Set(current);
      for (const campaign of selectedPageRows) {
        if (checked) next.add(campaign.id);
        else next.delete(campaign.id);
      }
      return next;
    });
  }

  return (
    <div className="flex h-full flex-col gap-4 px-[22px] py-[18px]">
      <div data-slot="managed-group-basics" className="flex shrink-0 flex-col gap-2">
        <div className="flex w-[434px] flex-col gap-1.5">
          <Label htmlFor="managed-group-name" className="text-[11px] leading-[14px]">
            {t("create.name")}
          </Label>
          <Input
            id="managed-group-name"
            name="managed-group-name"
            autoComplete="off"
            value={name}
            maxLength={100}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder={t("create.namePlaceholder")}
          />
          <p className="text-[10px] leading-[15px] text-muted-foreground">{t("create.nameHint")}</p>
        </div>

        <div className="flex w-[434px] flex-col gap-1.5">
          <Label className="text-[11px] leading-[14px]">{t("create.shop")}</Label>
          <Select items={shopItems} value={shopId} onValueChange={onShopChange}>
            <SelectTrigger aria-label={t("create.shop")} className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {shopItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] leading-[15px] text-muted-foreground">{t("create.shopHint")}</p>
        </div>
      </div>

      <section
        aria-label={t("create.transferLabel")}
        className="flex h-[371px] shrink-0 flex-col overflow-hidden rounded-[10px] border border-line"
      >
        <ManagedGroupCandidateFilters
          layout="transfer"
          portfolioOptions={portfolioOptions}
          search={campaignSearch}
          deliveryStatus={campaignDeliveryStatus}
          portfolioId={campaignPortfolioId}
          aiStatus={campaignAiStatus}
          onSearchChange={onCampaignSearchChange}
          onDeliveryStatusChange={onCampaignDeliveryStatusChange}
          onPortfolioIdChange={onCampaignPortfolioIdChange}
          onAiStatusChange={onCampaignAiStatusChange}
        />

        <div
          data-slot="campaign-transfer-grid"
          className="grid min-h-0 flex-1 grid-cols-[420px_42px_420px]"
        >
          <ManagedGroupCandidatePicker
            layout="transfer"
            showFilters={false}
            title={t("create.availableCampaigns")}
            titleDescription={t("create.candidateFiltersApply")}
            campaigns={campaigns}
            portfolioOptions={portfolioOptions}
            search={campaignSearch}
            deliveryStatus={campaignDeliveryStatus}
            portfolioId={campaignPortfolioId}
            aiStatus={campaignAiStatus}
            sortBy={campaignSortBy}
            sortDirection={campaignSortDirection}
            checkedIds={new Set(pendingAdd.keys())}
            isCampaignError={isCampaignError}
            isPortfolioError={isPortfolioError}
            isLoading={isLoading}
            emptyText={
              campaignSearch.trim() ? t("create.noSearchResults") : t("create.noCampaigns")
            }
            page={campaignPage}
            pageSize={campaignPageSize}
            total={campaignTotal}
            onSearchChange={onCampaignSearchChange}
            onDeliveryStatusChange={onCampaignDeliveryStatusChange}
            onPortfolioIdChange={onCampaignPortfolioIdChange}
            onAiStatusChange={onCampaignAiStatusChange}
            onSortChange={onCampaignSortChange}
            onPageChange={onCampaignPageChange}
            onRetryCampaigns={onRetryCampaigns}
            onRetryPortfolios={onRetryPortfolios}
            onToggleAll={toggleAllPendingAdd}
            onToggle={togglePendingAdd}
          />

          <fieldset
            data-slot="campaign-transfer-controls"
            data-orientation="vertical"
            aria-label={t("create.transferActions")}
            className="flex w-[42px] flex-col items-center justify-center gap-[5px] border-x border-badge-neutral-border bg-table-footer"
          >
            <Button
              size="icon"
              className="size-[25px] rounded-[7px]"
              aria-label={t("create.addSelectedCampaigns")}
              disabled={pendingAdd.size === 0}
              onClick={() => {
                onAddCampaigns([...pendingAdd.values()]);
                setPendingAdd(new Map());
              }}
            >
              <IconChevronRight
                aria-hidden
                width={13}
                height={13}
                viewBox="0 0 24 24"
                strokeWidth={1.7}
              />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="size-[25px] rounded-[7px]"
              aria-label={t("create.removeSelectedCampaigns")}
              disabled={pendingRemove.size === 0}
              onClick={() => {
                onRemoveCampaigns([...pendingRemove]);
                setPendingRemove(new Set());
              }}
            >
              <IconChevronLeft
                aria-hidden
                width={13}
                height={13}
                viewBox="0 0 24 24"
                strokeWidth={1.7}
              />
            </Button>
          </fieldset>

          <CampaignTransferPane
            kind="selected"
            title={t("create.selectedCampaigns")}
            countLabel={t("create.selected", { count: selected.length })}
            rows={selectedPageRows}
            checkedIds={pendingRemove}
            isError={false}
            isLoading={false}
            emptyText={t("create.noneSelected")}
            page={safeSelectedPage}
            pageCount={selectedPageCount}
            pageSize={CAMPAIGN_PAGE_SIZE}
            total={selected.length}
            sortBy={campaignSortBy}
            sortDirection={campaignSortDirection}
            checkboxLabel={(campaign) => t("create.selectForRemove", { name: campaign.name })}
            selectAllLabel={t("create.selectAllSelected")}
            allRowsChecked={selectedAllChecked}
            someRowsChecked={selectedSomeChecked}
            onPageChange={onSelectedCampaignPageChange}
            onToggleAll={toggleAllPendingRemove}
            onToggle={(campaign) => togglePendingRemove(campaign.id)}
            onSortChange={onCampaignSortChange}
          />
        </div>
      </section>
    </div>
  );
}
